import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./StudentDashboard.css";

const StudentDashboard = () => {
    const navigate = useNavigate();

    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [projects, setProjects] = useState([]);

    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingProjects, setLoadingProjects] = useState(false);

    const [showCreate, setShowCreate] = useState(false);
    const [projectName, setProjectName] = useState("");
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            setLoadingEvents(true);
            setError("");

            const response = await fetch("/api/events", {
                credentials: "include"
            });

            const data = await response.json();
            //console.log(`EVEVT DATA=${data}`)

            if (!response.ok) {
                throw new Error(data.error || "Unable to load events");
            }

            setEvents(data.events || []);

            // Automatically select the first active event
            const activeEvent = (data.events || []).find(
                event => event.status === "active"
            );

            if (activeEvent) {
                selectEvent(activeEvent);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingEvents(false);
        }
    };

    const selectEvent = async (event) => {
        setSelectedEvent(event);
        setLoadingProjects(true);
        setProjects([]);
        setError("");

        try {
            const response = await fetch(
                `/api/student/projects?event_id=${event.id}`,
                {
                    credentials: "include"
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || "Unable to load projects");
            }

            setProjects(data.projects || []);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingProjects(false);
        }
    };

    const createProject = async () => {
        if (!projectName.trim()) {
            return;
        }

        try {
            setCreating(true);
            setError("");

            const response = await fetch("/api/student/projects", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    event_id: selectedEvent.id,
                    name: projectName.trim()
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error || "Unable to create project"
                );
            }

            setShowCreate(false);
            setProjectName("");

            // Open newly created project
            if (data.project?.id) {
                navigate(`/rect/app/projects/${data.project.id}`);
            } else {
                selectEvent(selectedEvent);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setCreating(false);
        }
    };

    const openProject = (project) => {
        navigate(`/rect/app/projects/${project.id}`);
    };

    const getStatusClass = (status) => {
        return `status status-${status || "unknown"}`;
    };

    return (
        <div className="student-dashboard">

            {/* Header */}
            <div className="dashboard-header">
                <div>
                    <h1>Student Dashboard</h1>
                    <p>
                        Select an event and manage your projects.
                    </p>
                </div>
            </div>

            {error && (
                <div className="dashboard-error">
                    {error}
                </div>
            )}

            {/* Events */}
            <section className="events-section">

                <div className="section-header">
                    <div>
                        <h2>Available Events</h2>
                        <p>Select an event to view your projects.</p>
                    </div>
                </div>

                {loadingEvents ? (
                    <div className="loading">
                        Loading events...
                    </div>
                ) : events.length === 0 ? (
                    <div className="empty-state">
                        <h3>No events available</h3>
                        <p>
                            There are currently no events available.
                        </p>
                    </div>
                ) : (
                    <div className="event-grid">

                        {events.map(event => (
                            <div
                                key={event.id}
                                className={
                                    selectedEvent?.id === event.id
                                        ? "event-card selected"
                                        : "event-card"
                                }
                                onClick={() => selectEvent(event)}
                            >
                                <div className="event-card-header">
                                    <span className="event-number">
                                        EVENT {event.id}
                                    </span>

                                    <span className={getStatusClass(event.status)}>
                                        {event.status}
                                    </span>
                                </div>

                                <h3>{event.name}</h3>

                                {event.description && (
                                    <p className="event-description">
                                        {event.description}
                                    </p>
                                )}

                                <div className="event-info">

                                    <div>
                                        <span className="info-label">
                                            Lab
                                        </span>
                                        <span>
                                            {event.lab?.name ||
                                                event.lab_name ||
                                                `Lab ${event.lab_id}`}
                                        </span>
                                    </div>

                                    {event.lab?.robot && (
                                        <div>
                                            <span className="info-label">
                                                Robot
                                            </span>
                                            <span>
                                                {event.lab.robot}
                                            </span>
                                        </div>
                                    )}

                                </div>

                                <button
                                    className="event-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        selectEvent(event);
                                    }}
                                >
                                    {selectedEvent?.id === event.id
                                        ? "Selected"
                                        : "Open Event"}
                                </button>

                            </div>
                        ))}

                    </div>
                )}
            </section>

            {/* Selected Event */}
            {selectedEvent && (
                <section className="student-projects-section">

                    <div className="selected-event">

                        <div className="selected-event-title">
                            <span>Selected Event</span>
                            <h2>{selectedEvent.name}</h2>
                        </div>

                        <div className="selected-event-details">

                            <div>
                                <span>Lab</span>
                                <strong>
                                    {selectedEvent.lab?.name ||
                                        selectedEvent.lab_name ||
                                        `Lab ${selectedEvent.lab_id}`}
                                </strong>
                            </div>

                            {selectedEvent.lab?.robot && (
                                <div>
                                    <span>Robot</span>
                                    <strong>
                                        {selectedEvent.lab.robot}
                                    </strong>
                                </div>
                            )}

                            <div>
                                <span>Status</span>
                                <strong>
                                    {selectedEvent.status}
                                </strong>
                            </div>

                        </div>

                    </div>

                    <div className="student-projects-header">
                        <div>
                            <h2>My Projects</h2>
                            <p>
                                Projects created for {selectedEvent.name}
                            </p>
                        </div>

                        {selectedEvent.status === "active" && (
                            <button
                                className="create-button"
                                onClick={() => setShowCreate(true)}
                            >
                                + Create Project
                            </button>
                        )}
                    </div>

                    {loadingProjects ? (
                        <div className="loading">
                            Loading projects...
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="empty-projects">
                            <div className="empty-icon">
                                +
                            </div>

                            <h3>No projects yet</h3>

                            <p>
                                Create your first project for this event.
                            </p>

                            {selectedEvent.status === "active" && (
                                <button
                                    className="create-button"
                                    onClick={() => setShowCreate(true)}
                                >
                                    Create Your First Project
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="student-project-list">

                            {projects.map(project => (
                                <div
                                    className="student-project-card"
                                    key={project.id}
                                >

                                    <div className="student-project-main">

                                        <div className="student-project-icon">
                                            &lt;/&gt;
                                        </div>

                                        <div className="student-project-details">

                                            <h3>
                                                {project.name}
                                            </h3>

                                            <div className="student-project-meta">

                                                <span>
                                                    Branch:
                                                </span>

                                                <code>
                                                    {project.branch_name}
                                                </code>

                                            </div>

                                            {project.commit_hash && (
                                                <div className="student-project-meta">

                                                    <span>
                                                        Commit:
                                                    </span>

                                                    <code>
                                                        {project.commit_hash.substring(
                                                            0,
                                                            8
                                                        )}
                                                    </code>

                                                </div>
                                            )}

                                        </div>

                                    </div>

                                    <div className="student-project-right">

                                        <span
                                            className={getStatusClass(
                                                project.status
                                            )}
                                        >
                                            {project.status}
                                        </span>

                                        <button
                                            className="open-project-button"
                                            onClick={() =>
                                                openProject(project)
                                            }
                                        >
                                            Open
                                        </button>

                                    </div>

                                </div>
                            ))}

                        </div>
                    )}

                </section>
            )}

            {/* Create Project Modal */}
            {showCreate && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowCreate(false)}
                >
                    <div
                        className="create-modal"
                        onClick={(e) => e.stopPropagation()}
                    >

                        <h2>Create New Project</h2>

                        <p>
                            Create a new project for:
                        </p>

                        <div className="modal-event">
                            {selectedEvent?.name}
                        </div>

                        <label>
                            Project Name
                        </label>

                        <input
                            type="text"
                            value={projectName}
                            onChange={(e) =>
                                setProjectName(e.target.value)
                            }
                            placeholder="e.g. Navigation Test"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    createProject();
                                }
                            }}
                        />

                        <div className="modal-actions">

                            <button
                                className="cancel-button"
                                onClick={() => {
                                    setShowCreate(false);
                                    setProjectName("");
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="create-button"
                                disabled={
                                    creating ||
                                    !projectName.trim()
                                }
                                onClick={createProject}
                            >
                                {creating
                                    ? "Creating..."
                                    : "Create Project"}
                            </button>

                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};

export default StudentDashboard;